// src/store/slices/roleSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetch all roles
 */
export const fetchRoles = createAsyncThunk(
  'role/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/companies/roles`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch roles';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

/**
 * Create a new role
 */
export const createRole = createAsyncThunk(
  'role/createRole',
  async (roleData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/companies/roles`, roleData);
      dispatch(fetchRoles());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create role';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

/**
 * Update an existing role
 */
export const updateRole = createAsyncThunk(
  'role/updateRole',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      console.log('Updating role:', id, data); // Debug: payload sent
      const response = await axios.patch(`${API_URL}/companies/roles/${id}`, data);
      console.log('Update response:', response.data); // Debug: response received
      dispatch(fetchRoles());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update role';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

/**
 * Delete a role by ID
 */
export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/companies/roles/${id}`);
      dispatch(fetchRoles());
      return id;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete role';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

const roleSlice = createSlice({
  name: 'role',
  initialState: {
    list: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    clearRoleError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Roles
      .addCase(fetchRoles.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
        state.error = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Create Role
      .addCase(createRole.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(createRole.fulfilled, (state) => { state.status = 'succeeded'; state.error = null; })
      .addCase(createRole.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload; })

      // Update Role
      .addCase(updateRole.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(updateRole.fulfilled, (state) => { state.status = 'succeeded'; state.error = null; })
      .addCase(updateRole.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload; })

      // Delete Role
      .addCase(deleteRole.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.list = state.list.filter((r) => r.id !== action.payload);
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload; });
  },
});

export const { clearRoleError } = roleSlice.actions;
export default roleSlice.reducer;
